import { ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Column, Row } from '@tanstack/react-table';
import { isEmpty } from 'radash';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@/components/core/Popover';
import { LOCAL_STORAGE } from '@/constants.ts';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal.tsx';
import { useLocalStorage } from '@/hooks/useLocalStorage.ts';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser.tsx';

import { useValidationsStore,Validation } from '../hooks/useValidationsStore';

interface CollectionActionCell<TData> {
    row: Row<TData>;
    column: Column<TData>;
    remove: (rowIndex: number) => void,
}

const MultiDimensionalActionCell = <TData,>({ column, remove, row }: CollectionActionCell<TData>) => {
    const { t } = useTranslation();
    const { user } = useUser();
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);

    const [isDeleteConfirmationDisabled] =
        useLocalStorage<boolean>(
            `${LOCAL_STORAGE.DISABLE_DELETE_CONFIRMATION}${user?.id}`,
            false,
        );

    const handleRemove = (closeDialog = true) => {
        remove(row.index);
        if (closeDialog) setShowRemoveDialog(false);
    };

    const handleCancel = () => {
        setShowRemoveDialog(false);
    };

    const [localValidationBubbleOpen, setLocalValidationBubbleOpen] = useState(false);
    const fieldPath = column.columnDef.meta?.collectionAttributeFieldPath;
    const rowPath = `${fieldPath}.${row.index}`;
    const rowValidationErrors = useValidationsStore(useShallow(state => state.localValidationErrors[rowPath] as Validation[]));

    return (
        <div className="tw-flex tw-flex-row-reverse tw-justify-center tw-items-center">
            <div id={`row${row.id}-actions`} className="tw-flex tw-items-center tw-space-x-2">
                {
                    !isEmpty(rowValidationErrors) &&
                    <Popover open={localValidationBubbleOpen}>
                        <PopoverTrigger
                            asChild
                            onMouseEnter={() => setLocalValidationBubbleOpen(true)}
                            onMouseLeave={() => setLocalValidationBubbleOpen(false)} >
                            <div className="tw-cursor-pointer">
                                <Icon icon={ExclamationTriangleIcon} variant="destructive" size="md" />
                            </div>
                        </PopoverTrigger>
                        <PopoverPortal>
                            <PopoverContent
                                side="left"
                                sideOffset={-8}
                                className="tw-w-full tw-max-w-[60vw] tw-max-h-[50vh] tw-p-6 tw-overflow-y-auto tw-whitespace-pre-wrap"
                                onMouseEnter={() => setLocalValidationBubbleOpen(true)}
                                onMouseLeave={() => setLocalValidationBubbleOpen(false)}>
                                <ul>
                                    {rowValidationErrors.map((error, index) =>
                                        <li key={index} className="tw-text-t-sm">{t(error.message)}</li>
                                    )}
                                </ul>
                            </PopoverContent>
                        </PopoverPortal>
                    </Popover>
                }
                <IconButton
                    type="button"
                    data-testid={`delete-entry-${row.id}`}
                    className="tw-icon-primary"
                    icon={TrashIcon}
                    size="md"
                    variant="destructive"
                    onClick={() => isDeleteConfirmationDisabled ? handleRemove(false) : setShowRemoveDialog(true)}
                    title={t('ACTION.DELETE')}
                />
                <ConfirmationModal
                    title='GROUP_DELETE_ENTRY'
                    subTitle='EDITOR.DELETE_CONFIRMATION_TEXT.ROW'
                    discardBtnText = 'ACTION.DELETE'
                    closeBtnText ='CANCEL'
                    btnVariant="destructive"
                    show={showRemoveDialog}
                    onSubmit={handleRemove}
                    onClose={handleCancel}
                />
            </div>
        </div>
    );
};

export { MultiDimensionalActionCell };
